import PropTypes from 'prop-types';
import React, { useRef } from 'react';

import {
  Attribution, VimeoVideo,
} from '@powdr/components';
import { useSeason } from '@powdr/hooks';
import {
  colorProfileByIndex,
  getImageAltText,
  getImageAttribution,
} from '@powdr/utils';

import { HeroSlideContent } from './components';
import {
  StyledHeroSlide,
  BackgroundImage,
  BackgroundVideo,
  SlideContentWrapper,
  OpacityLayer,
} from './styles';

export const HeroSlide = ({
  slide: {
    id,
    title,
    copy,
    isDisableTextShadow,
    copyBackgroundColor,
    buttonText,
    buttonHref,
    buttonTarget,
    vimeoLink,
    vimeoLinkMobile,
    contentAlignment,
    heroIcon,
    heroIconWidth,
    componentSeason,
    imageWidth,
    imageWidthMobile,
    backgroundVideoLink,
    backgroundVideoLinkMobile,
    embeddedVideoLink,
    slideOpacityPercentage,
    slideOpacityColor,
    relationships,
  },
  isEnableMobileVideoAutoplay = false,
  slideOffsetDirection = null,
  slideOffsetAdjustment = null,
  isAltCopyDisplay = false,
}) => {
  const { checkSeasonality } = useSeason();
  const slideRef = useRef(null);
  const { gatsbyImage: image } = relationships?.primaryImage?.relationships?.image || {};
  const { gatsbyImage: mobileImage } = relationships?.mobileImage?.relationships?.image || {};
  const hasOverlayImage = relationships?.overlayImage?.gatsbyImage
    || relationships?.overlayImage?.imageSrc;
  const imageInContent = hasOverlayImage && (title || copy || buttonText);
  const copyBackgroundColorValue = copyBackgroundColor
    ? colorProfileByIndex(copyBackgroundColor)
    : null;

  return (
    checkSeasonality(componentSeason) && (
      <StyledHeroSlide
        key={id}
        ref={slideRef}
        isVimeoBackground={!!(vimeoLink || vimeoLinkMobile)}
      >
        {/* Attribution */}
        {['mobile', 'desktop'].map((responsiveSize) => (
          <Attribution key={responsiveSize} className={responsiveSize}>
            {getImageAttribution(
              responsiveSize === 'mobile' && mobileImage
                ? relationships?.mobileImage?.relationships?.image
                : relationships?.primaryImage?.relationships?.image,
            )}
          </Attribution>
        ))}

        {/* Background Image */}
        {['mobile', 'desktop'].map((responsiveSize) => (
          <BackgroundImage
            key={responsiveSize}
            className={responsiveSize}
            image={responsiveSize === 'mobile' ? mobileImage || image : image}
            loading="eager"
            alt={getImageAltText(
              responsiveSize === 'mobile' && mobileImage
                ? relationships?.mobileImage?.relationships?.image
                : relationships?.primaryImage?.relationships?.image,
            )}
            $slideOffsetDirection={slideOffsetDirection}
            $slideOffsetAdjustment={slideOffsetAdjustment}
            $isAltCopyDisplay={responsiveSize === 'desktop' ? isAltCopyDisplay : false}
          />
        ))}

        {/* Background Video */}
        {['mobile', 'desktop'].map((responsiveSize) => {
          const isMobile = responsiveSize === 'mobile';
          const vimeo = isMobile ? vimeoLinkMobile || vimeoLink : vimeoLink;
          const video = isMobile
            ? backgroundVideoLinkMobile || (backgroundVideoLink && isEnableMobileVideoAutoplay)
            : backgroundVideoLink;

          return (
            <BackgroundVideo
              key={responsiveSize}
              className={responsiveSize}
              $slideOffsetDirection={slideOffsetDirection}
              $slideOffsetAdjustment={slideOffsetAdjustment}
              $isAltCopyDisplay={isMobile ? false : isAltCopyDisplay}
            >
              {!vimeo && video && (
              <video autoPlay loop muted playsInline>
                <source src={video} />
              </video>
              )}
              {vimeo && <VimeoVideo parentRef={slideRef} videoLink={vimeo} isBackground />}
            </BackgroundVideo>
          );
        })}

        {/* Background Opacity */}
        {slideOpacityPercentage > 0 && (
          <OpacityLayer
            $opacity={slideOpacityPercentage || 0}
            $color={slideOpacityColor || '#000'}
          />
        )}

        {/* Slide Content */}
        {['mobile', 'desktop'].map((responsiveSize) => (
          <SlideContentWrapper
            key={responsiveSize}
            className={responsiveSize}
            $slideOffsetDirection={slideOffsetDirection}
            $slideOffsetAdjustment={slideOffsetAdjustment}
            $hasOverlayImage={hasOverlayImage && !imageInContent}
            $isAltCopyDisplay={responsiveSize === 'desktop' ? isAltCopyDisplay : false}
            $contentAlignment={contentAlignment}
          >
            <HeroSlideContent
              isMobile={responsiveSize === 'mobile'}
              slideOffsetDirection={slideOffsetDirection}
              slideOffsetAdjustment={slideOffsetAdjustment}
              isAltCopyDisplay={isAltCopyDisplay}
              contentAlignment={contentAlignment}
              imageInContent={imageInContent}
              hasOverlayImage={hasOverlayImage}
              embeddedVideoLink={embeddedVideoLink}
              title={title}
              copy={copy}
              heroIcon={heroIcon}
              heroIconWidth={heroIconWidth}
              buttonHref={buttonHref}
              buttonTarget={buttonTarget}
              buttonText={buttonText}
              relationships={relationships}
              imageWidth={imageWidth}
              imageWidthMobile={imageWidthMobile}
              copyBackgroundColor={copyBackgroundColor}
              copyBackgroundColorValue={copyBackgroundColorValue}
              isDisableTextShadow={isDisableTextShadow}
            />
          </SlideContentWrapper>
        ))}
      </StyledHeroSlide>
    )
  );
};

HeroSlide.propTypes = {
  slide: PropTypes.shape().isRequired,
  isEnableMobileVideoAutoplay: PropTypes.bool,
  slideOffsetDirection: PropTypes.string,
  slideOffsetAdjustment: PropTypes.number,
  isAltCopyDisplay: PropTypes.bool,
};
